import React from "react";
import styles from "../stylesERv2.module.scss";

/**
 * Detalle de "Comisión comercial" (Bloque A) del ER v2 para un mes: resumen por ejecutivo +
 * tabla por oportunidad/cuota facturada (con su desglose por ejecutivo). Solo lectura.
 *
 * PASO 6 (reingeniería del EBITDA, 2026-10-07) — fuente única: `filas` llega YA filtrada al mes
 * (ventas + cuotas de contratos combinadas) desde `index.js`, con el MISMO `montoLocal`/desglose
 * que suma la celda (`chileVentaLeavesSesion.registros["A - Publicidad / Comisión comercial"]`
 * para Chile; el equivalente reconstruido sobre los hooks viejos para Perú, que no migra). El
 * modal ya NO filtra `oportunidades` ni llama a `calculateSalesCommission`/
 * `getSalesCommissionBreakdown` por su cuenta — "Total mes" = Σ `fila.totalComision` = la celda,
 * por construcción. Antes de este paso, el desglose se armaba con `getSalesCommissionBreakdown`
 * SIN `pctMap` mientras el total usaba `calculateSalesCommission` CON `pctMap` — divergían en
 * cualquier venta con `comision_snapshot` sellado (ver doc de cierre, ejemplo real: celda
 * $483.246 vs. "Total mes" $301.059).
 */
export const DetalleComisionComercialModal = ({
  isOpen,
  mes,
  filas = [],
  users,
  moneda,
  formatCurrency,
  pais,
  onClose,
}) => {
  if (!isOpen) return null;

  const resumenEjecutivos = filas.reduce((acc, fila) => {
    (fila.desglose || []).forEach((item) => {
      const nombreEjecutivo = users[item.ejecutivo]?.nombre || `Ejecutivo ${item.ejecutivo}`;
      acc[nombreEjecutivo] = (acc[nombreEjecutivo] || 0) + item.comision;
    });
    return acc;
  }, {});

  const totalMes = filas.reduce((sum, f) => sum + (f.totalComision || 0), 0);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContentComisionComercial} onClick={(e) => e.stopPropagation()}>
        <h3>Detalle comisión comercial — {mes}</h3>

        {/* RESUMEN POR EJECUTIVO */}
        <div className={styles.comercialSummary}>
          <h4>Resumen por Ejecutivo</h4>
          {Object.keys(resumenEjecutivos).length === 0 ? (
            <div style={{ color: "#666", fontStyle: "italic" }}>No hay comisiones en este mes</div>
          ) : (
            Object.entries(resumenEjecutivos).map(([ejecutivo, total]) => (
              <div key={ejecutivo} className={styles.comercialSummaryItem}>
                <strong>{ejecutivo}:</strong> {formatCurrency(total)}
              </div>
            ))
          )}
          <div
            className={styles.comercialSummaryItem}
            style={{ marginTop: "12px", fontSize: "1.1rem", borderTop: "1px solid #ccc", paddingTop: "8px" }}
          >
            <strong>Total mes:</strong> {formatCurrency(totalMes)}
          </div>
        </div>

        {/* TABLA DETALLE */}
        <table className={styles.modalTable}>
          <thead>
            <tr>
              <th className={styles.comercialNumeroHeader}>Número Factura</th>
              <th className={styles.comercialClienteHeader}>Cliente Facturado</th>
              <th className={styles.comercialImporteHeader}>Importe Facturado</th>
              <th className={styles.comercialAgenciaHeader}>Comisión Agencia</th>
              <th className={styles.comercialEdificioHeader}>Comisión Edificio</th>
              <th className={styles.comercialNetoHeader}>Importe Neto</th>
              <th className={styles.comercialEjecutivoHeader}>Ejecutivo Asociado</th>
              <th className={styles.comercialPorcentajeHeader}>Comisión %</th>
              <th className={styles.comercialLocalHeader}>Comisión {moneda}</th>
              <th className={styles.comercialLocalHeaderTotal}>COMISIÓN TOTAL {moneda}</th>
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: "center", padding: "30px", color: "#666", fontSize: "1rem" }}>
                  No hay oportunidades facturadas en este mes
                </td>
              </tr>
            ) : (
              filas.map((fila, filaIdx) => {
                const desglose = fila.desglose || [];
                const numRows = desglose.length || 1;

                if (desglose.length === 0) {
                  return (
                    <tr key={`${fila.id}-${filaIdx}`}>
                      <td className={styles.comercialNumeroData}>{fila.facturanumero || "-"}</td>
                      <td className={styles.comercialClienteData}>{fila.cliente || "Sin cliente"}</td>
                      <td className={styles.comercialImporteData}>
                        {fila.moneda_dolar
                          ? `US$ ${(fila.importeOriginalMoneda || 0).toLocaleString(pais === "Chile" ? "es-CL" : "es-PE", { maximumFractionDigits: 0 })}`
                          : formatCurrency(fila.importe || 0)}
                      </td>
                      <td className={styles.comercialAgenciaData}>{formatCurrency(fila.comisionAgencia || 0)}</td>
                      <td className={styles.comercialEdificioData}>{formatCurrency(fila.comisionEdificios || 0)}</td>
                      <td className={styles.comercialNetoData}>{formatCurrency(fila.neto || 0)}</td>
                      <td className={styles.comercialEjecutivoData}>
                        {fila.ejecutivo ? users[fila.ejecutivo]?.nombre || "Sin Ejecutivo" : "Sin Ejecutivo"}
                      </td>
                      <td className={styles.comercialPorcentajeData}>-</td>
                      <td className={styles.comercialLocalData}>{formatCurrency(0)}</td>
                      <td className={styles.comercialLocalDataTotal}>{formatCurrency(0)}</td>
                    </tr>
                  );
                }

                return desglose.map((item, idx) => (
                  <tr key={`${fila.id}-${filaIdx}-${idx}`}>
                    {idx === 0 && (
                      <>
                        <td rowSpan={numRows} className={styles.comercialNumeroData}>
                          {fila.facturanumero || "-"}
                        </td>
                        <td rowSpan={numRows} className={styles.comercialClienteData}>
                          {fila.cliente || "Sin cliente"}
                        </td>
                        <td rowSpan={numRows} className={styles.comercialImporteData}>
                          {fila.moneda_dolar
                            ? `US$ ${(fila.importeOriginalMoneda || 0).toLocaleString(pais === "Chile" ? "es-CL" : "es-PE", { maximumFractionDigits: 0 })}`
                            : formatCurrency(fila.importe || 0)}
                        </td>
                        <td rowSpan={numRows} className={styles.comercialAgenciaData}>
                          {formatCurrency(fila.comisionAgencia || 0)}
                        </td>
                        <td rowSpan={numRows} className={styles.comercialEdificioData}>
                          {formatCurrency(fila.comisionEdificios || 0)}
                        </td>
                        <td rowSpan={numRows} className={styles.comercialNetoData}>
                          {formatCurrency(fila.neto || 0)}
                        </td>
                      </>
                    )}
                    <td className={styles.comercialEjecutivoData}>
                      {users[item.ejecutivo]?.nombre || `Ejecutivo ${item.ejecutivo}`}
                    </td>
                    <td className={styles.comercialPorcentajeData}>{item.percentage.toFixed(2)}%</td>
                    <td className={styles.comercialLocalData}>{formatCurrency(item.comision)}</td>
                    {idx === 0 && (
                      <td rowSpan={numRows} className={styles.comercialLocalDataTotal}>
                        {formatCurrency(fila.totalComision)}
                      </td>
                    )}
                  </tr>
                ));
              })
            )}
          </tbody>
        </table>
        <div className={styles.modalActions}>
          <button className={styles.btnClose} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
